import { useState, useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Box, Group, Select, Text, Loader, Paper, Stack, Badge } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useGetParentLoginStatsQuery } from "../redux/features/parentApi";

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Composant de graphique pour les statistiques de connexion d'un parent spécifique
 */
const ParentLoginStatsChart = ({ parentId, parentName }) => {
  // État pour les filtres
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 30)),
    new Date(),
  ]);
  const [groupBy, setGroupBy] = useState("day");
  const [chartType, setChartType] = useState("bar");

  // Formater les dates pour l'API
  const formatDate = (date) => {
    if (!date) return null;
    return date.toISOString().split("T")[0];
  };

  // Requête API
  const { data, isLoading, error } = useGetParentLoginStatsQuery(
    {
      parentId,
      startDate: formatDate(dateRange[0]),
      endDate: formatDate(dateRange[1]),
      groupBy,
    },
    {
      skip: !parentId,
    }
  );

  // Préparer les données du graphique
  const chartData = useMemo(() => {
    if (!data?.data?.stats) return null;

    const stats = data.data.stats;
    
    if (stats.length === 0) return null;

    const labels = stats.map((s) => {
      const date = new Date(s.date);
      if (groupBy === "month") {
        return date.toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
      } else if (groupBy === "week") {
        return `Sem. ${date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}`;
      }
      return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
    });

    return {
      labels,
      datasets: [
        {
          label: "Connexions",
          data: stats.map((s) => s.count),
          borderColor: "rgb(34, 197, 94)",
          backgroundColor: chartType === "line" 
            ? "rgba(34, 197, 94, 0.1)" 
            : "rgba(34, 197, 94, 0.7)",
          fill: chartType === "line",
          tension: 0.4,
        },
      ],
    };
  }, [data, groupBy, chartType]);

  // Options du graphique
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack spacing="md">
        {/* En-tête */}
        <Group position="apart">
          <Box>
            <Text weight={600} size="lg">
              Historique de connexion
            </Text>
            {parentName && (
              <Text size="sm" color="dimmed">{parentName}</Text>
            )}
          </Box>
          {data?.data && (
            <Group spacing="lg">
              <Box ta="center">
                <Text size="xs" color="dimmed">Total connexions</Text>
                <Badge size="lg" color="green" variant="filled">
                  {data.data.total_connections}
                </Badge>
              </Box>
              {data.data.last_login && (
                <Box ta="center">
                  <Text size="xs" color="dimmed">Dernière connexion</Text>
                  <Text size="sm" weight={500}>
                    {new Date(data.data.last_login).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Text>
                </Box>
              )}
            </Group>
          )}
        </Group>

        {/* Filtres */}
        <Group>
          <DatePickerInput
            type="range"
            label="Période"
            placeholder="Sélectionner une période"
            value={dateRange}
            onChange={setDateRange}
            clearable={false}
            style={{ flex: 1, maxWidth: 280 }}
            size="sm"
          />
          <Select
            label="Grouper par"
            value={groupBy}
            onChange={setGroupBy}
            data={[
              { value: "day", label: "Jour" },
              { value: "week", label: "Semaine" },
              { value: "month", label: "Mois" },
            ]}
            style={{ width: 120 }}
            size="sm"
          />
          <Select
            label="Type"
            value={chartType}
            onChange={setChartType}
            data={[
              { value: "bar", label: "Barres" },
              { value: "line", label: "Ligne" },
            ]}
            style={{ width: 100 }}
            size="sm"
          />
        </Group>

        {/* Graphique */}
        <Box style={{ height: 250 }}>
          {isLoading ? (
            <Group position="center" style={{ height: "100%" }}>
              <Loader size="sm" />
            </Group>
          ) : error ? (
            <Group position="center" style={{ height: "100%" }}>
              <Text color="red" size="sm">Erreur lors du chargement</Text>
            </Group>
          ) : chartData ? (
            chartType === "line" ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <Bar data={chartData} options={chartOptions} />
            )
          ) : (
            <Group position="center" style={{ height: "100%" }}>
              <Text color="dimmed" size="sm">
                {data?.data?.total_connections === 0 
                  ? "Aucune connexion sur cette période" 
                  : "Sélectionnez une période"}
              </Text>
            </Group>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export default ParentLoginStatsChart;
